import { Modal, message } from "antd";
import { useEffect } from "react";
import { resetDemo } from "../../service/demo.service";

/** Written after a reset so the presenter's other tabs follow it (``storage``
 * events reach every other tab of the origin, never the writer). */
export const DEMO_RESET_KEY = "nudge.demo_reset_at";

/**
 * Demo mode (backend ``demo_ehr``): clear what this presenter made and reseed a
 * fresh demo day around now. Other presenters in the clinic are untouched.
 */
export const confirmDemoReset = () => {
  Modal.confirm({
    title: "Reset this demo?",
    content:
      "Clears the patients, notes and chats you've made and puts the demo patients back on today's schedule. Other presenters aren't affected.",
    okText: "Reset demo",
    okButtonProps: { danger: true },
    cancelText: "Cancel",
    onOk: async () => {
      try {
        const { data } = await resetDemo();
        message.success(`Demo reset · ${data.patients} patients on today's schedule`);
        try {
          localStorage.setItem(DEMO_RESET_KEY, String(Date.now()));
        } catch {
          // Storage unavailable: other tabs simply keep their old view.
        }
        // A full reload drops every trace of the old day at once: transfer
        // banners, the Superdoc thread, the open note, the cached client list.
        setTimeout(() => window.location.assign("/home"), 800);
      } catch (error) {
        const detail = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
        message.error(detail || "The demo couldn't be reset. Try again.");
        // Rejecting keeps the dialog open so the presenter can retry.
        throw error;
      }
    },
  });
};

/**
 * Demo accounts: when this presenter resets in another tab, reload this one into
 * the fresh day instead of leaving it on retired patients and notes.
 */
export const useDemoResetFollower = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === DEMO_RESET_KEY) window.location.assign("/home");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [enabled]);
};

/** The header menu's "Reset demo" row; inert while a recording is running. */
const DemoResetMenuItem = ({ disabled }: { disabled: boolean }) => (
  <div onClick={disabled ? undefined : confirmDemoReset} style={{ color: disabled ? undefined : "#000" }}>
    Reset demo
  </div>
);

export default DemoResetMenuItem;
